import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Smartphone,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SpinningIco } from "@/components/brand/SpinningIco";
import { Wordmark } from "@/components/brand/Wordmark";
import { supabase } from "@/lib/supabase";

/**
 * /auth/callback - universal landing page for email-confirmation links.
 *
 * Handles two Supabase email flows that share this redirect:
 *   1. Sign-up confirmation  (type=signup)
 *   2. Email-change verification  (type=email_change)
 *
 * When a user clicks an email link, Supabase's verify endpoint
 * validates the token then redirects here with `?code=xxx` (PKCE
 * flow). The browser SDK's detectSessionInUrl picks that up
 * automatically and either:
 *   - succeeds → there's now a session for the user → success state
 *   - fails → the code_verifier isn't in this browser's localStorage
 *     (likely the user signed up on the mobile app and is now
 *     clicking the link in a desktop browser) → fail state explaining
 *     that they need to open the link on the device they signed up on
 *
 * This is the SAFETY NET page. The intended landing for signups is
 * `whistll://auth/callback` (the mobile deep link); this web URL only
 * gets hit when that deep link can't resolve (custom URL schemes
 * silently fail in many email clients in 2026).
 */
export function AuthCallbackPage() {
  type State =
    | { kind: "verifying" }
    | { kind: "success" }
    | { kind: "error"; message: string; reason: "expired" | "wrong-client" | "unknown" };

  const [state, setState] = useState<State>({ kind: "verifying" });

  useEffect(() => {
    // Surface a Supabase-emitted error in the URL before doing anything.
    const params = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const errDescription =
      params.get("error_description") ?? hash.get("error_description");
    if (errDescription) {
      setState({
        kind: "error",
        message: String(errDescription),
        reason: errDescription.toLowerCase().includes("expired")
          ? "expired"
          : "unknown",
      });
      return;
    }

    // If there's no code in the URL at all, the user probably hit this
    // page directly - not via an email click.
    if (!params.get("code") && !hash.get("access_token")) {
      setState({
        kind: "error",
        message:
          "This page is the landing for Whistll email confirmations. If you got here by accident, you can close the tab.",
        reason: "unknown",
      });
      return;
    }

    // Let detectSessionInUrl do its job, then check whether a session
    // landed. We listen to onAuthStateChange so we don't race.
    let settled = false;
    const sub = supabase.auth.onAuthStateChange((event, session) => {
      if (settled) return;
      if (
        session &&
        (event === "SIGNED_IN" ||
          event === "USER_UPDATED" ||
          event === "TOKEN_REFRESHED")
      ) {
        settled = true;
        setState({ kind: "success" });
      }
    });

    const timeout = window.setTimeout(() => {
      if (settled) return;
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (settled) return;
        settled = true;
        if (session) {
          setState({ kind: "success" });
        } else {
          setState({
            kind: "error",
            message:
              "This confirmation link can only be opened on the device you used to sign up. If you signed up in the Whistll app, open this email on your phone with the app installed.",
            reason: "wrong-client",
          });
        }
      });
    }, 4000);

    return () => {
      sub.data.subscription.unsubscribe();
      window.clearTimeout(timeout);
    };
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 py-12">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-[640px] opacity-30 blur-3xl"
        style={{
          backgroundImage:
            "radial-gradient(closest-side at 50% 0%, hsl(var(--volt)), transparent 70%)",
        }}
      />

      <div className="w-full max-w-md">
        <div className="mb-10 flex flex-col items-center gap-3">
          <SpinningIco size={120} />
          <Wordmark size="text-3xl" />
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-2xl sm:p-8">
          {state.kind === "verifying" && (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-volt" />
              <p className="text-sm text-muted-foreground">
                Verifying your email…
              </p>
            </div>
          )}

          {state.kind === "success" && (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-volt text-primary-foreground">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">Email verified</h2>
                <p className="text-sm text-muted-foreground">
                  You're all set. Open the Whistll app to sign in, or carry on
                  here if you're a venue partner.
                </p>
              </div>
              <div className="flex flex-col items-stretch gap-2 self-stretch pt-2">
                <Button asChild size="lg">
                  <Link to="/dashboard">Continue to dashboard</Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/">Back to homepage</Link>
                </Button>
              </div>
            </div>
          )}

          {state.kind === "error" && (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-volt/30 bg-volt-subtle text-volt">
                {state.reason === "wrong-client" ? (
                  <Smartphone className="h-6 w-6" />
                ) : (
                  <AlertTriangle className="h-6 w-6" />
                )}
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">
                  {state.reason === "expired"
                    ? "Link expired"
                    : state.reason === "wrong-client"
                      ? "Open this in the Whistll app"
                      : "Couldn't verify"}
                </h2>
                <p className="text-sm text-muted-foreground">{state.message}</p>
              </div>
              {state.reason === "wrong-client" && (
                <p className="text-xs text-muted-foreground">
                  Tip: forward this email to yourself on the phone with Whistll
                  installed, then tap the button there.
                </p>
              )}
              <Button asChild variant="outline" size="lg">
                <Link to="/">Back to homepage</Link>
              </Button>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Stuck? Email{" "}
          <a
            href="mailto:hello@whistll.com?subject=Email%20verification"
            className="underline underline-offset-2 hover:text-foreground"
          >
            hello@whistll.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}
